import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  Visibility,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import MessageApi from "Services/Message";
import MessageViewModal from "Pages/Modals/Messages/View";
import MessageDeleteModal from "Pages/Modals/Messages/Delete";

const Messages = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMessageModal, setViewMessageModal] = useState(false);
  const [deleteMessageModal, setDeleteMessageModal] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState("");

  const normalizeMessageList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.data?.messages)) {
      return { rows: payload.data.messages || [], total: payload?.data?.total || payload?.data?.messages?.length || 0 };
    }
    if (Array.isArray(payload?.messages)) {
      return { rows: payload.messages || [], total: payload?.total || payload?.messages?.length || 0 };
    }
    return { rows: [], total: 0 };
  };

  const getMessages = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    MessageApi.getMessageList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeMessageList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllMessages = useCallback(() => {
    MessageApi.getMessageList({ page: 1, limit: 1000, search: "" }).then(
      (res) => {
        const normalized = normalizeMessageList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getMessages();
    getAllMessages();
  }, [getMessages, getAllMessages]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    messageId: data?.id || "-",
    conversationId: data?.conversation_id || data?.conversations?.id || "-",
    sender: `${data?.users?.first_name || ""} ${data?.users?.last_name || ""}`.trim() || data?.sender_id || "-",
    body: data?.body || "-",
    system: data?.is_system_message ? "Yes" : "No",
    readAt: data?.read_at ? new Date(data.read_at).toLocaleString() : "-",
    bookingStatus: data?.conversations?.bookings?.status || "-",
    rideStatus: data?.conversations?.rides?.status || "-",
    createdAt: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
    raw: data,
  }));

  const formatted = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Message ID": r.messageId,
    "Conversation ID": r.conversationId,
    Sender: r.sender,
    Message: r.body,
    "System Message": r.system,
    "Read At": r.readAt,
    "Booking Status": r.bookingStatus,
    "Ride Status": r.rideStatus,
    Created: r.createdAt,
  }));

  const bulkFormatted = _.orderBy(bulkrow || [], ["created_at"], ["asc"]).map((data, index) => ({
    "S.NO": index + 1,
    "Message ID": data?.id || "-",
    "Conversation ID": data?.conversation_id || data?.conversations?.id || "-",
    Sender: `${data?.users?.first_name || ""} ${data?.users?.last_name || ""}`.trim() || data?.sender_id || "-",
    Message: data?.body || "-",
    "System Message": data?.is_system_message ? "Yes" : "No",
    "Read At": data?.read_at ? new Date(data.read_at).toLocaleString() : "-",
    "Booking Status": data?.conversations?.bookings?.status || "-",
    "Ride Status": data?.conversations?.rides?.status || "-",
    Created: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
  }));

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleView = (messageRow) => {
    const messageId = messageRow?.id || messageRow?._id;
    if (!messageId) {
      dispatch(
        showSnackbar({
          message: "Message ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedMessageId(messageId);
    setViewMessageModal(true);
  };

  const handleDelete = (messageRow) => {
    const messageId = messageRow?.id || messageRow?._id;
    if (!messageId) {
      dispatch(
        showSnackbar({
          message: "Message ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedMessageId(messageId);
    setDeleteMessageModal(true);
  };

  const handleCloseView = () => {
    setViewMessageModal(false);
    setSelectedMessageId("");
  };

  const handleCloseDelete = (deleted = false) => {
    setDeleteMessageModal(false);
    setSelectedMessageId("");
    if (deleted) {
      getMessages();
      getAllMessages();
    }
  };

  const handleCopy = () => {
    const header = Object.keys(formatted[0] || {}).join("\t");
    const rows = formatted.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Messages");
    XLSX.writeFile(workbook, "MessageList.xlsx");
  };
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "MessageList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.autoTable({
      head: [["S.NO", "Message ID", "Conversation ID", "Sender", "Message", "System", "Read At", "Booking Status", "Ride Status", "Created"]],
      body: formatted.map((r) => Object.values(r)),
      styles: { fontSize: 8 },
    });
    doc.save("MessageList.pdf");
  };
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All MessageList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    { field: "messageId", headerName: "Message ID", minWidth: 220, sortable: true },
    { field: "conversationId", headerName: "Conversation ID", minWidth: 220, sortable: true },
    { field: "sender", headerName: "Sender", minWidth: 170, sortable: true },
    { field: "body", headerName: "Message", minWidth: 260, sortable: false },
    { field: "system", headerName: "System", minWidth: 90, sortable: true },
    { field: "readAt", headerName: "Read At", minWidth: 170, sortable: true },
    { field: "bookingStatus", headerName: "Booking Status", minWidth: 120, sortable: true },
    { field: "rideStatus", headerName: "Ride Status", minWidth: 120, sortable: true },
    { field: "createdAt", headerName: "Created", minWidth: 180, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button variant="contained" color="primary" size="small" style={{ minWidth: 36, padding: "4px 8px" }} onClick={() => handleView(params?.row?.raw)}>
            <Visibility fontSize="small" />
          </Button>
          <Button variant="contained" color="error" size="small" style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }} onClick={() => handleDelete(params?.row?.raw)}>
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={6} md={6} className={classes.addBtn}></Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => {
              const p = parseInt(newPage, 10) + 1;
              if (p >= 1) setPage(p);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => setSearchText(model?.quickFilterValues?.join(" ") || "")}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>

      <MessageViewModal openDialog={viewMessageModal} handleClose={handleCloseView} messageId={selectedMessageId} />
      <MessageDeleteModal openDialog={deleteMessageModal} handleClose={handleCloseDelete} messageId={selectedMessageId} />
    </Grid>
  );
};

export default Messages;
